import { useCallback, useEffect, useMemo, useState } from "react";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

// PR #54 — Per-tenant Chains UX. A brand sees and manages their own chain
// mappings as a pure overlay on the canonical graph. Editing a canonical chain
// (rename / archive / reassign a store) auto-clones it into the tenant overlay
// server-side; canonical is never mutated. Backed by /api/tenant/chains/* and
// /api/tenant/stores/:id/chain*.

interface OverlayChain {
  chain_id: string;
  tenant_id: string;
  name: string;
  slug: string | null;
  source: "tenant" | "canonical";
  cloned_from_canonical_chain_id: string | null;
  source_chain_name: string | null;
  is_overridden: boolean;
  archived_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  store_count: number;
}

interface MemberRow {
  tenant_store_id: string;
  is_explicit_member: boolean;
  chain_source: "tenant" | "canonical";
  store_status: string;
  store_display_name: string | null;
  resolved_display_name: string | null;
  license_number: string | null;
  wtb_display_name: string | null;
  cm_display_name: string | null;
  gbp_name: string | null;
  license_dba: string | null;
}

interface HistoryRow {
  id: string;
  changed_at: string;
  change_type: string;
  source: string;
  notes: string | null;
  changed_by_email: string | null;
  _store?: string | null;
}

type Filter = "all" | "mine" | "canonical" | "overridden" | "archived";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "mine", label: "My Chains" },
  { key: "canonical", label: "Canonical" },
  { key: "overridden", label: "Overridden" },
  { key: "archived", label: "Archived" },
];

function sourceTag(c: OverlayChain): { label: string; cls: string } {
  if (c.source === "canonical") return { label: "Canonical", cls: "bg-sky-50 text-sky-700 border-sky-200" };
  if (c.is_overridden) return { label: "Overridden", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Tenant", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

function bestName(m: MemberRow): string {
  return (
    m.resolved_display_name ||
    m.store_display_name ||
    m.wtb_display_name ||
    m.cm_display_name ||
    m.license_dba ||
    m.gbp_name ||
    m.license_number ||
    m.tenant_store_id.slice(0, 8)
  );
}

export default function MyChains() {
  const { context } = useSDK();
  const toast = useToast();

  // PR #66: defense-in-depth. A brand session must never see this canonical
  // admin overlay view — App.tsx's synchronous route guard should redirect
  // first, but if it doesn't (stale bundle, direct mount) bounce to the
  // brand-scoped chain list before any admin data loads.
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j) return;
        if (j.user?.role !== "admin" && j.tenant?.slug) {
          window.location.replace(`#/brand/${j.tenant.slug}/my-chains`);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [chains, setChains] = useState<OverlayChain[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  // Full non-archived list (filter-independent) that powers the reassign dropdown.
  const [assignable, setAssignable] = useState<OverlayChain[]>([]);

  // Drawer state.
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ chain: OverlayChain; members: MemberRow[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState<"members" | "history">("members");
  const [history, setHistory] = useState<HistoryRow[] | null>(null);
  const [renameVal, setRenameVal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ filter });
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(context, `/api/tenant/chains?${params.toString()}`);
      setChains(r.rows || []);
    } catch (err) {
      toast.push(`Failed to load chains: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [context, filter, q, toast]);

  const loadAssignable = useCallback(async () => {
    try {
      const r = await api.get(context, `/api/tenant/chains?filter=all`);
      setAssignable((r.rows || []).filter((c: OverlayChain) => !c.archived_at));
    } catch {
      // non-fatal — the dropdown just won't have options
    }
  }, [context]);

  // Debounce filter + search into one reload.
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    loadAssignable();
  }, [loadAssignable]);

  const openChain = useCallback(
    async (chainId: string) => {
      setOpenId(chainId);
      setTab("members");
      setHistory(null);
      setDetailLoading(true);
      setDetail(null);
      try {
        const r = await api.get(context, `/api/tenant/chains/${chainId}`);
        setDetail(r);
        setRenameVal(r.chain?.name ?? "");
      } catch (err) {
        toast.push(`Failed to open chain: ${(err as Error).message}`, "error");
        setOpenId(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [context, toast]
  );

  const closeDrawer = () => {
    setOpenId(null);
    setDetail(null);
    setHistory(null);
  };

  const refreshAll = useCallback(
    async (reopenId?: string) => {
      await Promise.all([load(), loadAssignable()]);
      if (reopenId) await openChain(reopenId);
    },
    [load, loadAssignable, openChain]
  );

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const r = await api.post(context, `/api/tenant/chains`, { name });
      toast.push(`Created "${name}".`, "success");
      setNewName("");
      await refreshAll();
      if (r.chain?.chain_id) await openChain(r.chain.chain_id);
    } catch (err) {
      toast.push(`Create failed: ${(err as Error).message}`, "error");
    } finally {
      setCreating(false);
    }
  };

  const rename = async () => {
    if (!detail) return;
    const name = renameVal.trim();
    if (!name || name === detail.chain.name) return;
    setBusy(true);
    try {
      const r = await api.patch(context, `/api/tenant/chains/${detail.chain.chain_id}`, { name });
      toast.push(r.cloned ? "Renamed (canonical chain cloned to your overlay)." : "Renamed.", "success");
      const newId = r.chain?.chain_id ?? detail.chain.chain_id;
      await refreshAll(newId);
    } catch (err) {
      toast.push(`Rename failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async () => {
    if (!detail) return;
    const c = detail.chain;
    setBusy(true);
    try {
      if (c.archived_at) {
        await api.post(context, `/api/tenant/chains/${c.chain_id}/unarchive`, {});
        toast.push("Unarchived.", "success");
        await refreshAll(c.chain_id);
      } else {
        const r = await api.post(context, `/api/tenant/chains/${c.chain_id}/archive`, {});
        toast.push(r.cloned ? "Archived (canonical chain cloned first)." : "Archived.", "success");
        const newId = r.chain?.chain_id ?? c.chain_id;
        await refreshAll(newId);
      }
    } catch (err) {
      toast.push(`Action failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const reassign = async (storeId: string, chainId: string) => {
    setBusy(true);
    try {
      const r = await api.post(context, `/api/tenant/stores/${storeId}/chain`, {
        chain_id: chainId || null,
      });
      toast.push(
        chainId
          ? r.cloned
            ? "Reassigned (canonical chain cloned to your overlay)."
            : "Reassigned."
          : "Cleared — store reverted to canonical.",
        "success"
      );
      await refreshAll(openId ?? undefined);
    } catch (err) {
      toast.push(`Reassign failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (storeId: string) => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.del(context, `/api/tenant/chains/${detail.chain.chain_id}/members/${storeId}`);
      toast.push("Removed — store reverted to canonical.", "success");
      await refreshAll(detail.chain.chain_id);
    } catch (err) {
      toast.push(`Remove failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const loadHistory = useCallback(async () => {
    if (!detail) return;
    const members = detail.members;
    const all: HistoryRow[] = [];
    await Promise.all(
      members.map(async (m) => {
        try {
          const r = await api.get(context, `/api/tenant/stores/${m.tenant_store_id}/chain-history`);
          for (const row of (r.rows || []) as HistoryRow[]) all.push({ ...row, _store: bestName(m) });
        } catch {
          // skip a store that fails
        }
      })
    );
    all.sort((a, b) => (a.changed_at < b.changed_at ? 1 : a.changed_at > b.changed_at ? -1 : 0));
    setHistory(all.slice(0, 200));
  }, [context, detail]);

  useEffect(() => {
    if (tab === "history" && history === null && detail) loadHistory();
  }, [tab, history, detail, loadHistory]);

  const counts = useMemo(() => {
    return {
      total: chains.length,
      stores: chains.reduce((n, c) => n + (c.store_count || 0), 0),
    };
  }, [chains]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">My Chains</h2>
          <p className="text-sm text-gray-600 mt-1">
            {loading
              ? "Loading…"
              : `${counts.total.toLocaleString()} chain${counts.total === 1 ? "" : "s"} · ${counts.stores.toLocaleString()} store${counts.stores === 1 ? "" : "s"} mapped`}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Editing a canonical chain creates your own private copy — the shared graph is never changed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
            placeholder="New chain name…"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-accent-500/50"
          />
          <Button variant="primary" onClick={create} disabled={creating || !newName.trim()}>
            {creating ? "Creating…" : "Create chain"}
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                active
                  ? "bg-accent-50 text-accent-700 border-accent-200 ring-1 ring-inset ring-accent-200"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          );
        })}
        <div className="ml-auto">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chains…"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-accent-500/50"
          />
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500 text-sm">Loading chains…</div>
      ) : chains.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
          <p className="text-gray-700 text-sm font-medium">No chains here.</p>
          <p className="text-gray-500 text-xs mt-1">
            {filter === "all"
              ? "Once your stores map to chains, they'll appear here. Create a chain to group stores manually."
              : "Nothing matches this filter."}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">Chain</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-500">Source</th>
                <th className="px-4 py-2.5 text-right font-medium text-gray-500">Stores</th>
                <th className="px-4 py-2.5 text-right font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {chains.map((c) => {
                const tag = sourceTag(c);
                return (
                  <tr key={c.chain_id} className="hover:bg-gray-50/70">
                    <td className="px-4 py-3">
                      <button onClick={() => openChain(c.chain_id)} className="text-left font-medium text-gray-900 hover:text-accent-700">
                        {c.name}
                      </button>
                      {c.archived_at && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                          Archived
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tag.cls}`}>{tag.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">{(c.store_count || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openChain(c.chain_id)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openId && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={closeDrawer} />
          <div className="relative z-50 h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {detail ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${sourceTag(detail.chain).cls}`}>
                          {sourceTag(detail.chain).label}
                        </span>
                        {detail.chain.archived_at && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">Archived</span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          value={renameVal}
                          onChange={(e) => setRenameVal(e.target.value)}
                          className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent-500/50"
                        />
                        <Button variant="secondary" size="sm" onClick={rename} disabled={busy || !renameVal.trim() || renameVal.trim() === detail.chain.name}>
                          Save
                        </Button>
                      </div>
                      {detail.chain.source === "canonical" && (
                        <p className="mt-1 text-xs text-gray-500">Saving will create your own editable copy of this canonical chain.</p>
                      )}
                    </>
                  ) : (
                    <h3 className="text-lg font-semibold text-gray-900">Loading…</h3>
                  )}
                </div>
                <button onClick={closeDrawer} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Close">
                  ✕
                </button>
              </div>
              {detail && (
                <div className="mt-3 flex items-center gap-2">
                  <Button variant={detail.chain.archived_at ? "secondary" : "danger-ghost"} size="sm" onClick={toggleArchive} disabled={busy}>
                    {detail.chain.archived_at ? "Unarchive" : "Archive"}
                  </Button>
                  <div className="ml-auto inline-flex rounded-md border border-gray-200 p-0.5">
                    {(["members", "history"] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`rounded px-3 py-1 text-xs font-medium capitalize ${tab === t ? "bg-accent-500 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4">
              {detailLoading || !detail ? (
                <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
              ) : tab === "members" ? (
                detail.members.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-500">No stores resolve to this chain.</div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {detail.members.map((m) => (
                      <li key={m.tenant_store_id} className="py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-medium text-gray-900">{bestName(m)}</p>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {m.license_number || "no license"}
                              {" · "}
                              {m.is_explicit_member ? "explicit member" : "inherited from canonical"}
                            </p>
                          </div>
                          {m.is_explicit_member && (
                            <Button variant="danger-ghost" size="sm" onClick={() => removeMember(m.tenant_store_id)} disabled={busy}>
                              Remove
                            </Button>
                          )}
                        </div>
                        <div className="mt-2">
                          <ChainAssignSelect
                            currentChainId={detail.chain.chain_id}
                            options={assignable}
                            disabled={busy}
                            onAssign={(chainId) => reassign(m.tenant_store_id, chainId)}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )
              ) : history === null ? (
                <div className="py-10 text-center text-sm text-gray-500">Loading history…</div>
              ) : history.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">No chain changes recorded for these stores.</div>
              ) : (
                <ul className="space-y-3">
                  {history.map((h) => (
                    <li key={h.id} className="rounded-md border border-gray-100 bg-gray-50/60 px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-gray-800">{h.notes || h.change_type}</span>
                        <span className="text-xs text-gray-400">{new Date(h.changed_at).toLocaleString()}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {h._store ? `${h._store} · ` : ""}
                        {h.source}
                        {h.changed_by_email ? ` · ${h.changed_by_email}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChainAssignSelect({
  currentChainId,
  options,
  disabled,
  onAssign,
}: {
  currentChainId: string;
  options: OverlayChain[];
  disabled?: boolean;
  onAssign: (chainId: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-gray-500">
      Chain
      <select
        value={currentChainId}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v !== currentChainId) onAssign(v);
        }}
        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-accent-500/50 disabled:opacity-50"
      >
        <option value="">— No chain (revert to canonical) —</option>
        {options.map((c) => (
          <option key={c.chain_id} value={c.chain_id}>
            {c.name} {c.source === "canonical" ? "(canonical)" : c.is_overridden ? "(overridden)" : ""}
          </option>
        ))}
      </select>
    </label>
  );
}
